import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { DateWithDayOfWeekPipe } from '../../../../services/dateWithDayOfWeek';
import { ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TimesheetStaffDeniedReasonsComponent } from '../timesheet-staff-deniedreasons/timesheet-staff-deniedreasons.component';
import { TimesheetStaffErrorcodeComponent } from '../timesheet-staff-errorcode/timesheet-staff-errorcode.component';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import CommonUtil from '../../../../shared/authorization/common.util';

@Component({
  selector: 'ppl-timesheet-adjust-void-entry',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FormsModule,
    TableModule,
    NzToolTipModule,
    DateWithDayOfWeekPipe,
    TimesheetStaffDeniedReasonsComponent,
    TimesheetStaffErrorcodeComponent,
    DatePickerModule
  ],
  templateUrl: './timesheet-adjust-void-entry.component.html',
  styleUrl: './timesheet-adjust-void-entry.component.css'
})
export class TimesheetAdjustVoidEntryComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private serviceCodesSub?: Subscription;
  @Input() timesheetData?: any;
  @Input() searchParams: any;
  @Input() actionData: any;
  @Input() source?: string;
  @Output() timesheetAdjustedOrVoided = new EventEmitter();
  adjustVoidTimesheetForm!: FormGroup;
  reasonList: any;
  buttonLabel = '';
  actionType = '';
  serviceCodes: LookupValueItem[] = [];
  serviceLocations: any[] = [];
  serviceDescriptions: any[] = [];
  serviceCodesData: any[] = [];
  // Read-only display fields driven by the selected Service Description (authList entry).
  selectedPplAuthNum = '';
  selectedRegionalCenter = '';
  programName!: string;
  message: string = '';
  mcoList: any;
  isAuthFail = false;
  insufficientAuthMessage = 'Error: No Authorization for pay period and participant.';
  hasTimesheetAdjustError = false;
  timesheetAdjustErrorMessage = '';
  reasonText = '';
  compareServiceCode = (o1: any, o2: any): boolean => o1?.lkpValueCode === o2?.lkpValueCode;
  timeInTimeOutEqualMessage = 'Service Time In cannot be equal to Service Time Out.';
  timeOutLessThanTimeInMessage = 'Service Time In cannot be greater than Service Time Out.';
  timeOutOverNightShiftMessage = 'Overnight shifts must be entered separately after midnight (12:00 AM).';

  // OTE entries are copied from the parent timesheet, so their time in/out and
  // clock locations carry no real meaning and are shown as '-'.
  get isOvertimeEntry(): boolean {
    return CommonUtil.isOvertimeRow(this.timesheetData);
  }

  constructor(
    private fb: FormBuilder,
    private dateTimeFormatterService: DateTimeFormatterService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';

    this.actionType = this.actionData.buttonAction;
    this.buttonLabel = this.actionData.button_label;
    const isPayrollAdjust = this.source === 'payroll' && this.actionType?.toLowerCase().includes('adjust');
    const rawReasons = isPayrollAdjust
      ? this.lookupService.getLookupValue('Timesheet_Adjustment_Reason')
      : this.source === 'payroll' || this.source === 'billing'
      ? this.lookupService.getLookupValue('adjustment_reason')
      : this.lookupService.getLookupValue('resurrect_reason');
    this.reasonList = this.sortReasonsOtherLast(rawReasons);
    this.reasonText = this.actionType.indexOf('adjust') > -1 ? 'Adjust\nreason' : this.actionType.indexOf('void') > -1 ? 'Void\nreason' : 'Resurrect\nreason';

    this.adjustVoidTimesheetForm = this.fb.group({
      serviceDate: [null],
      serviceCode: [null],
      serviceDescription: [null],
      clockInLoc: [null],
      clockOutLoc: [null],
      timeIn: [null],
      timeOut: [null],
      totalTime: [{ value: null, disabled: true }],
      billedUnits: [null],
      reason: [null, [Validators.required]],
      mcoName: [null],
      reasonDetails: [null, [Validators.maxLength(160)]],
      crmNumber: [null, (this.source === 'payroll' && (this.actionType?.toLowerCase().includes('adjust') || this.actionType?.toLowerCase().includes('void'))) ? [Validators.required, Validators.maxLength(50)] : [Validators.maxLength(50)]],
    });

    this.adjustVoidTimesheetForm.get('reason')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((reason: any) => {
        const reasonDetailsControl = this.adjustVoidTimesheetForm.get('reasonDetails');
        const reasonName: string = reason?.lkpValueName ?? '';
        if (reasonName === 'Other' || reasonName.toLowerCase().startsWith('other')) {
          reasonDetailsControl?.setValidators([Validators.required, Validators.maxLength(160)]);
        } else {
          reasonDetailsControl?.setValidators([Validators.maxLength(160)]);
        }
        reasonDetailsControl?.updateValueAndValidity();
      });

    if (this.source == 'payroll' || this.source == 'resurrect') {
      this.setPayrollResurrectConfiguration();
    } else {
      this.setBillingConfiguration();
    }

    if (this.actionType === 'adjustAllTimesheet' || this.actionType === 'voidAllTimesheets' || this.actionType === 'adjustAllBilling' ||
      this.actionType === 'voidAllBillings' || this.actionType == 'adjustAllBillings' || this.actionType === 'resurrectAllTimesheet') {
      if (this.timesheetData?.timesheetLineIds?.length > 0) {
        this.message = `Note: The ${this.actionData.button_label} action will be performed on all selected timesheets.`;
      } else {
        this.message = `Note: The ${this.actionData.button_label} action will be performed on all timesheets matching the selected search criteria.`;
      }
    }
  }

  setBillingConfiguration() {
    this.lookupService.getOrgDataByType(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.mcoList = data.mcoEntries;
      }
    });
  }

  setPayrollResurrectConfiguration() {
    this.adjustVoidTimesheetForm.get('billedUnits')?.disable();

    if (this.actionType === 'adjustTimesheet' || this.actionType === 'resurrectTimesheet') {
      this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');
      this.serviceLocations = this.lookupService.getLookupValue('service_location');

      this.loadServiceCodesByConsumer(this.timesheetData.serviceDate, true);

      const clockInLoc = this.serviceLocations.find(loc => loc.lkpValueName?.toLowerCase() === this.timesheetData?.clockInLoc?.toLowerCase())?.lkpValueName ?? this.serviceLocations.find(loc => loc.lkpValueCode?.toLowerCase() === this.timesheetData?.clockInLoc?.toLowerCase())?.lkpValueName;
      const clockOutLoc = this.serviceLocations.find(loc => loc.lkpValueName?.toLowerCase() === this.timesheetData?.clockOutLoc?.toLowerCase())?.lkpValueName ?? this.serviceLocations.find(loc => loc.lkpValueCode?.toLowerCase() === this.timesheetData?.clockOutLoc?.toLowerCase())?.lkpValueName;

      this.adjustVoidTimesheetForm?.patchValue({
        serviceDate: this.timesheetData?.serviceDate,
        serviceCode: this.timesheetData?.serviceCode,
        clockInLoc: clockInLoc ?? null,
        clockOutLoc: clockOutLoc ?? null,
        timeIn: this.timesheetData?.timeIn ? this.convertTimeStringToDate(this.timesheetData.timeIn) : null,
        timeOut: this.timesheetData?.timeOut ? this.convertTimeStringToDate(this.timesheetData.timeOut) : null,
        billedUnits: this.timesheetData?.billedUnits
      });

      this.checkService();

      this.adjustVoidTimesheetForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.checkService());

      // Subscribed after the prefill patch above so opening the screen doesn't wipe the existing values.
      // A new service date can change which auths apply, so the user must pick a new code/auth.
      this.adjustVoidTimesheetForm.get('serviceDate')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((date: Date | null) => {
          this.adjustVoidTimesheetForm.patchValue({ serviceCode: null, serviceDescription: null });
          this.serviceDescriptions = [];
          this.serviceCodesData = [];
          this.setAuthDisplayFields(null);
          if (date) {
            this.loadServiceCodesByConsumer(this.dateTimeFormatterService.formatDateToString(new Date(date)), false);
          }
        });

      const requiredFields = ['serviceDate', 'serviceCode', 'serviceDescription', 'clockInLoc', 'clockOutLoc', 'timeIn', 'timeOut', 'billedUnits'];
      requiredFields.forEach(field => {
        this.adjustVoidTimesheetForm.get(field)?.setValidators([Validators.required]);
        this.adjustVoidTimesheetForm.get(field)?.updateValueAndValidity();
      });

      // For single timesheet adjust, clock locations and service time in/out are
      // prefilled from the timesheet and must not be editable.
      if (this.actionType === 'adjustTimesheet') {
        ['clockInLoc', 'clockOutLoc', 'timeIn', 'timeOut'].forEach(field => {
          this.adjustVoidTimesheetForm.get(field)?.disable({ emitEvent: false });
        });
      }

      this.timesheetData.timeIn = this.dateTimeFormatterService.convertTo12HourFormat(this.timesheetData.timeIn);
      this.timesheetData.timeOut = this.dateTimeFormatterService.convertTo12HourFormat(this.timesheetData.timeOut);
    }
  }

  /** prefillFromTimesheet: pre-select the timesheet's existing service description (initial load only). */
  private loadServiceCodesByConsumer(serviceDate: string, prefillFromTimesheet: boolean): void {
    this.isAuthFail = false;
    // Re-filter from the full lookup each time, since a previous date may have narrowed the list.
    const allServiceCodes: LookupValueItem[] = this.lookupService.getLookupValue('timesheet_filter_serviceCode') ?? [];
    const payload = {
      consumerId: this.timesheetData.participantId,
      periodFromDate: serviceDate,
      periodToDate: serviceDate,
      programCode: this.programName,
      providerId: this.timesheetData.providerId,
      source: "timesheet"
    };

    // Cancel any in-flight request so a slower response for an older date can't win.
    this.serviceCodesSub?.unsubscribe();
    this.serviceCodesSub = this.timesheetService
      .getServiceCodesByConsumer(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if ((data.responsedata == "Auth Fail" && data.status == 404) ||
          !data.responsedata || (Array.isArray(data.responsedata) && data.responsedata.length === 0)) {
          this.isAuthFail = true;
          this.serviceCodes = [];
          return;
        }

        this.serviceCodesData = data.responsedata;
        this.serviceCodes = allServiceCodes.filter(sc =>
          data.responsedata.some((item: any) => item.serviceCodeDetailId.toString() === sc.lkpValueCode)
        );

        if (!prefillFromTimesheet) return;

        const matchedServiceCode = data.responsedata.find((item: any) =>
          item.serviceCodeDetailId.toString() === this.timesheetData?.serviceCode?.lkpValueCode
        );
        this.serviceDescriptions = matchedServiceCode?.authList ?? [];

        const matchedDescription = this.serviceDescriptions.find((d: any) =>
          d.desc === this.timesheetData?.authDesc
        );
        if (matchedDescription) {
          this.adjustVoidTimesheetForm.patchValue({ serviceDescription: matchedDescription.ids });
          this.setAuthDisplayFields(matchedDescription);
        }
      });
  }

  checkService(): void {
    const timeIn = this.adjustVoidTimesheetForm.get('timeIn')?.value;
    const timeOut = this.adjustVoidTimesheetForm.get('timeOut')?.value;
    const serviceCode = this.adjustVoidTimesheetForm.get('serviceCode')?.value;

    if (!serviceCode || !timeIn || !timeOut) {
      this.adjustVoidTimesheetForm.get('totalTime')?.setValue(null, { emitEvent: false });
      this.adjustVoidTimesheetForm.get('billedUnits')?.setValue(null, { emitEvent: false });
      return;
    }

    if (timeOut <= timeIn) {
      return;
    }

    const totalTimeValue = this.dateTimeFormatterService.getDuration(new Date(timeIn), new Date(timeOut));
    this.adjustVoidTimesheetForm.get('totalTime')?.setValue(totalTimeValue, { emitEvent: false });
    this.adjustVoidTimesheetForm.get('billedUnits')?.setValue(1, { emitEvent: false });
  }

  /** Sorts reasons A→Z and pins any label starting with "Other" to the bottom of the list. */
  private sortReasonsOtherLast(reasons: LookupValueItem[] | null | undefined): LookupValueItem[] {
    if (!Array.isArray(reasons)) return [];
    const isOther = (r: LookupValueItem) => !!r?.lkpValueName?.toLowerCase()?.startsWith('other');
    const others = reasons.filter(isOther);
    const rest = reasons
      .filter(r => !isOther(r))
      .sort((a, b) => (a?.lkpValueName ?? '').localeCompare(b?.lkpValueName ?? ''));
    return [...rest, ...others];
  }

  onServiceCodeChange(serviceCode: LookupValueItem): void {
    const matched = this.serviceCodesData.find((item: any) =>
      item.serviceCodeDetailId.toString() === serviceCode?.lkpValueCode
    );
    this.serviceDescriptions = matched?.authList ?? [];
    this.adjustVoidTimesheetForm.patchValue({ serviceDescription: null });
    this.setAuthDisplayFields(null);
    this.checkService();
  }

  /** Updates the read-only PPL Authorization Number / Regional Center display for the selected Service Description. */
  onServiceDescriptionChange(ids: any): void {
    const matched = this.serviceDescriptions.find((d: any) => d.ids === ids);
    this.setAuthDisplayFields(matched);
  }

  private setAuthDisplayFields(authListItem: any): void {
    this.selectedPplAuthNum = authListItem?.pplAuthNum ?? '';
    this.selectedRegionalCenter = authListItem?.orgName ?? '';
  }

  isTimeInAndTimeOutInvalid(timeIn: Date, timeOut: Date) {
    this.adjustVoidTimesheetForm.get('timeIn')?.setErrors(null);
    this.adjustVoidTimesheetForm.get('timeOut')?.setErrors(null);

    const inHours = timeIn.getHours();
    const inMinutes = timeIn.getMinutes();
    const outHours = timeOut.getHours();
    const outMinutes = timeOut.getMinutes();

    const totalMinutesIn = inHours * 60 + inMinutes;
    const totalMinutesOut = outHours * 60 + outMinutes;

    if (totalMinutesIn === totalMinutesOut) {
      this.adjustVoidTimesheetForm.get('timeIn')?.setErrors({ customError: this.timeInTimeOutEqualMessage });

      return true;
    }

    if (totalMinutesOut < totalMinutesIn) {
      if (outHours < 8 || (outHours === 0 && outMinutes === 0)) {
        this.adjustVoidTimesheetForm.get('timeOut')?.setErrors({ customError: this.timeOutOverNightShiftMessage });
      } else {
        this.adjustVoidTimesheetForm.get('timeOut')?.setErrors({ customError: this.timeOutLessThanTimeInMessage });
      }

      return true;
    }

    return false;
  }

  /**
   * Resolves a clock location value to its display name. The value may arrive as a
   * lookup code (e.g. 'HME', 'CMT') or as an already-resolved name (e.g. 'Home', 'Community'),
   * so we match against both lkpValueName and lkpValueCode and always return the lkpValueName.
   */
  resolveLocationName(value: any): string {
    if (value == null || value === '') return '';
    const lowerValue = String(value).toLowerCase();
    return this.serviceLocations.find(loc => loc.lkpValueName?.toLowerCase() === lowerValue)?.lkpValueName
      ?? this.serviceLocations.find(loc => loc.lkpValueCode?.toLowerCase() === lowerValue)?.lkpValueName
      ?? value;
  }

  convertTimeStringToDate(timeString: string): Date {
    const currentDate = new Date();
    const [hours, minutes] = timeString.split(':');
    currentDate.setHours(Number(hours), Number(minutes), 0, 0); // Set the hours and minutes
    return currentDate;
  }

  onSave(): void {
    if (this.adjustVoidTimesheetForm?.valid) {
      let placeholder = '';
      let urlEndpoint = '';
      const reasonCode = this.adjustVoidTimesheetForm?.get('reason')?.value?.lkpValueCode;
      const reasonDetails = this.adjustVoidTimesheetForm?.get('reasonDetails')?.value?.trim();
      const payload: any = {
        reason: reasonDetails ? `${reasonCode} - ${reasonDetails}` : reasonCode
      };

      if (this.source == 'payroll' || this.source == 'resurrect') {
        const crmNumber = this.adjustVoidTimesheetForm?.get('crmNumber')?.value?.trim();
        if (this.source === 'payroll' && (this.actionType?.toLowerCase().includes('adjust') || this.actionType?.toLowerCase().includes('void')) && crmNumber) {
          payload.crmNumber = crmNumber;
        }
        const serviceTimeIn = this.adjustVoidTimesheetForm?.get('timeIn')?.value;
        const serviceTimeOut = this.adjustVoidTimesheetForm?.get('timeOut')?.value;

        if ((this.actionData.buttonAction === 'adjustTimesheet' || this.actionData.buttonAction === 'resurrectTimesheet') &&
          this.isTimeInAndTimeOutInvalid(new Date(serviceTimeIn), new Date(serviceTimeOut))) {
          return;
        }

        switch (this.actionData.buttonAction) {
          case 'adjustTimesheet': {
            placeholder = 'Timesheet adjusted';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_ADJUST;
            payload.timesheetId = this.timesheetData?.timesheetLineId;
            payload.dos = this.dateTimeFormatterService.formatToString(new Date(this.adjustVoidTimesheetForm?.get('serviceDate')?.value));
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            payload.tasAuthorizationDetailId = this.adjustVoidTimesheetForm?.get('serviceDescription')?.value;
            payload.clockInLocation = this.adjustVoidTimesheetForm?.get('clockInLoc')?.value;
            payload.clockOutLocation = this.adjustVoidTimesheetForm?.get('clockOutLoc')?.value;
            payload.clockIn = this.dateTimeFormatterService.formatTime(serviceTimeIn);
            payload.clockOut = this.dateTimeFormatterService.formatTime(serviceTimeOut);
            break;
          }
          case 'adjustAllTimesheet': {
            placeholder = 'Timesheet adjusted';
            if (this.timesheetData?.timesheetLineIds?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_ADJUST_TIMESHEET;
              payload.timesheetIDs = this.timesheetData?.timesheetLineIds;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_ADJUST_ALL_TIMESHEET;
              payload.searchParam = this.searchParams;
            }
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            break;
          }
          case 'voidTimesheet': {
            placeholder = 'Timesheet voided';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_TIMESHEET;
            payload.timesheetIDs = [this.timesheetData?.timesheetLineId];
            break;
          }
          case 'voidAllTimesheets': {
            placeholder = 'Timesheet voided';
            if (this.timesheetData?.timesheetLineIds?.length > 0) {
              payload.timesheetIDs = this.timesheetData?.timesheetLineIds;
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_TIMESHEET;
            } else {
              payload.searchParam = this.searchParams;
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_VOID_ALL_TIMESHEET;
            }
            break;
          }
          case 'resurrectTimesheet': {
            placeholder = 'Timesheet resurrected';
            urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT;
            payload.timesheetId = this.timesheetData?.timesheetLineId;
            payload.dos = this.dateTimeFormatterService.formatToString(new Date(this.adjustVoidTimesheetForm?.get('serviceDate')?.value));
            payload.serviceCode = this.adjustVoidTimesheetForm?.get('serviceCode')?.value?.lkpValueCode;
            payload.tasAuthorizationDetailId = this.adjustVoidTimesheetForm?.get('serviceDescription')?.value;
            payload.clockInLocation = this.adjustVoidTimesheetForm?.get('clockInLoc')?.value;
            payload.clockOutLocation = this.adjustVoidTimesheetForm?.get('clockOutLoc')?.value;
            payload.clockIn = this.dateTimeFormatterService.formatTime(serviceTimeIn);
            payload.clockOut = this.dateTimeFormatterService.formatTime(serviceTimeOut);
            break;
          }
          case 'resurrectAllTimesheet': {
            placeholder = 'Timesheet resurrected';
            if (this.timesheetData?.timesheetLineIds?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT_SELECTED;
              payload.timesheetIDs = this.timesheetData?.timesheetLineIds;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_RESURRECT_SEARCH_CRITERIA;
              payload.searchParam = this.searchParams;
            }
            break;
          }
        }
      } else {
        switch (this.actionData.buttonAction) {
          case 'voidBilling': {
            placeholder = 'Billing voided';
            urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_TIMESHEET;
            payload.timesheetIDs = [this.timesheetData?.timesheetLineId];
            break;
          }
          case 'voidAllBillings': {
            placeholder = 'Billings voided';
            if (this.timesheetData?.timesheetLineIds?.length > 0) {
              payload.timesheetIDs = this.timesheetData?.timesheetLineIds;
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_TIMESHEET;
            } else {
              payload.searchParam = this.searchParams;
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_VOID_ALL_TIMESHEET;
            }
            break;
          }
          case 'adjustAllBillings': {
            placeholder = 'Billings adjusted';
            if (this.timesheetData?.timesheetLineIds?.length > 0) {
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_ADJUST_TIMESHEET;
              payload.timesheetIDs = this.timesheetData?.timesheetLineIds;
            } else {
              urlEndpoint = ENDPOINTS.CLAIMS.BILLING_ADJUST_ALL_TIMESHEET;
              payload.searchParam = this.searchParams;
            }
            payload.mcoName = this.adjustVoidTimesheetForm?.get('mcoName')?.value;
            break;
          }
        }
      }

      if (urlEndpoint) {
        this.timesheetService.adjustVoidTimesheet(urlEndpoint, this.programName, payload, placeholder)
          .subscribe({
            next: () => {
              this.timesheetAdjustedOrVoided.emit({});
            },
            error: (error: any) => {
              if (error?.error?.responsedata == "TimesheetAdjust error" && error?.status == 409) {
                this.hasTimesheetAdjustError = true;
                this.timesheetAdjustErrorMessage = error.error.responsemsg[0]?.desc;
              }
            }
          });
      }
    } else {
      this.adjustVoidTimesheetForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
